import { useCallback, useEffect, useRef, useState } from "react";
import { employeeOnboardingGateway } from "../index";
import { stateName } from "../../people/stateNames";
import { StateElectionForm } from "../../people/components/StateElectionForm";
import {
  acceptsElections,
  describeAnswers,
  electionErrorMessage,
  formatDate,
} from "../../people/stateElections";
import type {
  SelfStateWithholdingElectionsResponse,
  StateCertificateEntry,
  StateWithholdingCatalogResponse,
  StateWithholdingStateView,
  StateWithholdingSubmissionDetail,
} from "../../people/types";
import "../../check-batch/check-batch.css";

const STATUS_WORDS: Record<StateWithholdingSubmissionDetail["status"], string> =
  {
    SubmittedPendingReview: "Waiting for review",
    Approved: "Approved",
    Rejected: "Not approved",
    Withdrawn: "Withdrawn",
  };

/** The catalog status in the employee's words. Nothing here invents a rule: it only restates the controlled catalog. */
function employeeCatalogMessage(
  entry: StateCertificateEntry | undefined,
  name: string,
): string {
  if (!entry)
    return `Escochex can't take a withholding election for ${name} here. Your payroll team handles it.`;
  switch (entry.status) {
    case "CertificateRequired":
      return `${name} needs your withholding election before its tax can be worked out.`;
    case "EmptyCertificateAccepted":
      return `${name} can work out your withholding without an election. Submit one only if you claim exemptions, extra withholding or exempt status.`;
    case "NoCertificateRequired":
      return acceptsElections(entry)
        ? `${name} can work out your withholding without an election. Submit one only if you choose a different rate or claim exempt status.`
        : `${name} withholds without an election from you, so there is nothing to submit.`;
    case "NotApplicable":
      return `${name} has no wage income tax, so there is no withholding election.`;
    case "NotEstablished":
      return `Escochex can't take ${name}'s withholding form yet. Your payroll team will follow up with you.`;
  }
}

function Answers({
  entry,
  answers,
  testId,
}: {
  entry: StateCertificateEntry | undefined;
  answers: StateWithholdingSubmissionDetail["answers"];
  testId: string;
}) {
  return (
    <dl className="sit__dl sit__dl--compact" data-testid={testId}>
      {describeAnswers(entry, answers).map((r) => (
        <div key={r.label}>
          <dt>{r.label}</dt>
          <dd>{r.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function SelfStateCard({
  stateCode,
  entry,
  view,
  onChanged,
}: {
  stateCode: string;
  entry: StateCertificateEntry | undefined;
  view: StateWithholdingStateView | undefined;
  onChanged: (message: string) => void;
}) {
  const [entering, setEntering] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const name = stateName(stateCode);
  const takes = entry !== undefined && acceptsElections(entry);
  const open = view?.openSubmission ?? null;
  const lastRejected = view?.history.find((h) => h.status === "Rejected");

  async function withdraw() {
    setBusy(true);
    setProblem(null);
    try {
      await employeeOnboardingGateway.withdrawMyStateWithholdingElection(
        stateCode,
      );
      onChanged(`Your ${name} submission was withdrawn.`);
    } catch (err) {
      setProblem(
        electionErrorMessage(
          err,
          "Your submission could not be withdrawn. Please try again.",
        ),
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      className="sit__block"
      aria-labelledby={`sse-${stateCode}-title`}
      data-testid={`sse-state-${stateCode}`}
    >
      <h4 id={`sse-${stateCode}-title`} className="sit__block-title">
        {name}
      </h4>
      <p className="muted">{employeeCatalogMessage(entry, name)}</p>

      {view?.current ? (
        <div data-testid={`sse-current-${stateCode}`}>
          <p>
            <strong>In effect</strong> since{" "}
            {formatDate(view.current.effectiveFrom)}
            {view.current.expiresOn
              ? `, until ${formatDate(view.current.expiresOn)}`
              : ""}
            .
          </p>
          <Answers
            entry={entry}
            answers={view.current.answers}
            testId={`sse-current-answers-${stateCode}`}
          />
        </div>
      ) : takes ? (
        <p data-testid={`sse-none-${stateCode}`}>
          You have no {name} election in effect.
        </p>
      ) : null}

      {view?.upcoming && (
        <p data-testid={`sse-upcoming-${stateCode}`}>
          Your newer election is approved and takes effect{" "}
          {formatDate(view.upcoming.effectiveFrom)}.
        </p>
      )}

      {open && (
        <article
          className="recip-review__card"
          aria-label={`Your ${name} submission waiting for review`}
          data-testid={`sse-open-${stateCode}`}
        >
          <header className="recip-review__head">
            <h5>Waiting for review</h5>
            <span className="recip-review__chip recip-review__chip--SubmittedPendingReview">
              Not used yet
            </span>
          </header>
          <p className="muted">
            Takes effect {formatDate(open.effectiveFrom)}.{" "}
            {open.source === "Employee"
              ? "You submitted it"
              : "Your payroll team entered it"}{" "}
            on {formatDate(open.submittedAtUtc)}. Your payroll team reviews it
            before it is used.
          </p>
          <Answers
            entry={entry}
            answers={open.answers}
            testId={`sse-open-answers-${stateCode}`}
          />
          {open.source === "Employee" && (
            <button
              type="button"
              className="sit__link"
              onClick={() => void withdraw()}
              disabled={busy}
            >
              {busy ? "Withdrawing…" : "Withdraw this submission"}
            </button>
          )}
        </article>
      )}

      {!open && lastRejected && !view?.current && (
        <p
          className="recip-review__warning"
          data-testid={`sse-rejected-${stateCode}`}
        >
          Your last {name} submission was not approved. Submit a new one, or ask
          your payroll team why.
        </p>
      )}

      {takes &&
        !open &&
        entry &&
        (entering ? (
          <StateElectionForm
            stateCode={stateCode}
            entry={entry}
            audience="employee"
            send={(input) =>
              employeeOnboardingGateway.submitMyStateWithholdingElection(input)
            }
            onCancel={() => setEntering(false)}
            onSubmitted={(m) => {
              setEntering(false);
              onChanged(m);
            }}
          />
        ) : (
          <button type="button" onClick={() => setEntering(true)}>
            {view?.current
              ? `Change my ${name} election`
              : `Submit my ${name} election`}
          </button>
        ))}

      {problem && (
        <p className="error" role="alert">
          {problem}
        </p>
      )}

      {view && view.history.length > 0 && (
        <details data-testid={`sse-history-${stateCode}`}>
          <summary>History ({view.history.length})</summary>
          <ul className="sit__fields">
            {view.history.map((h) => (
              <li key={h.submissionId} className="sit__field">
                <span>
                  <strong>{STATUS_WORDS[h.status]}</strong> · takes effect{" "}
                  {formatDate(h.effectiveFrom)} ·{" "}
                  {h.source === "Employee"
                    ? "submitted by you"
                    : "entered by your payroll team"}{" "}
                  on {formatDate(h.submittedAtUtc)}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}

/**
 * EPN-52 state withholding elections, employee self-service (PR 5). One card per state the employee works or lives in (derived by the server from the work
 * location and home address; the employee never picks another state). The form is the shared catalog-driven form, so it offers exactly the fields the
 * state's form takes. The employee picks today or later (SME-36 default), and a different user always approves (SME-34): a submission changes nothing
 * until approved. Reviewer ids are never shown. UX / DESIGN GAP: the referenced "State Income Tax (Figma 04)" frame has no effective date, review states
 * or history; this block reuses the administrator patterns until design supplies frames.
 */
export function StateElectionsSelfSection() {
  const [catalog, setCatalog] =
    useState<StateWithholdingCatalogResponse | null>(null);
  const [mine, setMine] =
    useState<SelfStateWithholdingElectionsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const messageRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      employeeOnboardingGateway.getStateWithholdingCatalog(),
      employeeOnboardingGateway.getMyStateWithholdingElections(),
    ])
      .then(([c, m]) => {
        if (cancelled) return;
        setCatalog(c);
        setMine(m);
        setLoadError(null);
      })
      .catch(() => {
        if (!cancelled)
          setLoadError(
            "Your state withholding elections could not be loaded. Please try again.",
          );
      });
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  const reload = useCallback(() => {
    setLoadError(null);
    setAttempt((n) => n + 1);
  }, []);

  const changed = useCallback(
    (m: string) => {
      setMessage(m);
      reload();
      setTimeout(() => messageRef.current?.focus(), 0);
    },
    [reload],
  );

  const codes = mine ? mine.derivedStates.map((d) => d.stateCode) : [];

  return (
    <section
      className="profile-section sit"
      aria-labelledby="sse-title"
      data-testid="state-elections-self"
    >
      <h3 id="sse-title" className="profile-section-title">
        State withholding elections
      </h3>
      {loadError ? (
        <>
          <p className="error" role="alert">
            {loadError}
          </p>
          <button type="button" onClick={reload}>
            Try again
          </button>
        </>
      ) : catalog === null || mine === null ? (
        <p className="muted" role="status">
          Loading your state withholding elections…
        </p>
      ) : (
        <>
          <p className="muted" data-testid="sse-intro">
            Tell us how you want each state to withhold income tax from your
            pay. Your payroll team reviews each submission before it is used.
          </p>
          <p
            ref={messageRef}
            tabIndex={-1}
            role="status"
            aria-live="polite"
            className="sit__live"
          >
            {message}
          </p>
          {codes.length === 0 ? (
            <p data-testid="sse-empty">
              We don&apos;t know your work or home state yet, so there is
              nothing to submit. Your payroll team sets your work location, and
              your home address comes from your profile.
            </p>
          ) : (
            codes.map((code) => (
              <SelfStateCard
                key={code}
                stateCode={code}
                entry={catalog.states.find((s) => s.code === code)}
                view={mine.states.find((s) => s.stateCode === code)}
                onChanged={changed}
              />
            ))
          )}
        </>
      )}
    </section>
  );
}
